/**
 * PWA Install Hook - Industry Standard Implementation
 * 
 * Based on:
 * - Google PWA Best Practices: https://web.dev/customize-install/
 * - Chrome DevRel Guidelines
 * - Patterns from Twitter PWA, Spotify Web, Instagram Web
 * 
 * Features:
 * - Captures beforeinstallprompt event
 * - Detects installation state (standalone mode)
 * - Tracks installation with appinstalled event
 * - Detects uninstall and re-shows prompt
 * - Progressive enhancement (works without prompt)
 * - Proper state management
 */

import { useState, useEffect, useCallback } from 'react';

import { logger, getErrorMessage } from '../utils/logger';
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  showInstallButton: boolean;
  install: () => Promise<void>;
  dismissInstallPrompt: () => void;
}

const STORAGE_KEY = 'pwa-install-state';

/**
 * Check if app is currently installed/running in standalone mode
 * Works for:
 * - Chrome/Edge: window.matchMedia
 * - Safari: window.navigator.standalone
 * - All: display-mode media query
 */
function isAppInstalled(): boolean {
  // Check if running in standalone mode (PWA installed)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // iOS Safari specific check
  if ('standalone' in window.navigator && (window.navigator as any).standalone === true) {
    return true;
  }
  
  // Check if opened from home screen (Android)
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  
  return false;
}

/**
 * Get stored install state
 * Used to track dismissals and detect uninstalls
 */
function getStoredState(): { dismissed: boolean; wasInstalled: boolean } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    logger.error('[PWA Install] Failed to read stored state:', { error: getErrorMessage(error) });
  }
  return { dismissed: false, wasInstalled: false };
}

/**
 * Save install state
 */
function saveState(dismissed: boolean, wasInstalled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissed, wasInstalled }));
  } catch (error) {
    logger.error('[PWA Install] Failed to save state:', { error: getErrorMessage(error) });
  }
}

/**
 * Custom hook for PWA installation
 * 
 * Industry Standard Pattern:
 * 1. Capture beforeinstallprompt on mount
 * 2. Check if already installed
 * 3. Provide install function
 * 4. Track installation
 * 5. Detect uninstall (wasInstalled=true but not standalone anymore)
 */
export function usePWAInstall(): PWAInstallState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    const installed = isAppInstalled();
    setIsInstalled(installed);
    
    // Get stored state
    const stored = getStoredState();
    
    // Detect uninstall: was installed before but not anymore
    if (stored.wasInstalled && !installed) {
      logger.debug('App was uninstalled, resetting state', { component: 'PWA Install' });
      saveState(false, false); // Clear dismissal on uninstall
      setIsDismissed(false);
    } else {
      setIsDismissed(stored.dismissed);
    }
    
    // Update stored installation state
    if (installed) {
      saveState(stored.dismissed, true);
    }
    
    /**
     * Capture beforeinstallprompt event
     * CRITICAL: Must be captured before it's fired (usually on page load)
     * Chrome/Edge fire this when PWA criteria are met
     */
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default mini-infobar from appearing on mobile
      e.preventDefault();
      
      logger.debug('beforeinstallprompt event captured', { component: 'PWA Install' });
      
      // Store the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    
    /**
     * Listen for app installation
     * Fired when user installs the PWA
     */
    const handleAppInstalled = () => {
      logger.debug('App installed successfully', { component: 'PWA Install' });
      
      setIsInstalled(true);
      setDeferredPrompt(null);
      saveState(false, true); // Clear dismissal, mark as installed
      
      // Optional: Track installation analytics
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', 'pwa_install', {
          event_category: 'engagement',
          event_label: 'PWA Installation'
        });
      }
    };
    
    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  /**
   * Trigger the install prompt
   * User-initiated action (button click)
   */
  const install = useCallback(async () => {
    if (!deferredPrompt) {
      logger.debug('No install prompt available', { component: 'PWA Install' });
      return;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for user response
      const choiceResult = await deferredPrompt.userChoice;
      
      logger.info('PWA install user choice', { outcome: choiceResult.outcome });
      
      if (choiceResult.outcome === 'accepted') {
        logger.debug('User accepted installation', { component: 'PWA Install' });
      } else {
        logger.debug('User dismissed installation', { component: 'PWA Install' });
      }
      
      // Clear the prompt as it can only be used once
      setDeferredPrompt(null);
      
    } catch (error) {
      logger.error('[PWA Install] Error showing prompt:', { error: getErrorMessage(error) });
    }
  }, [deferredPrompt]);

  /**
   * Dismiss install prompt
   * Hide button until next session or uninstall
   */
  const dismissInstallPrompt = useCallback(() => {
    logger.debug('User dismissed install prompt', { component: 'PWA Install' });
    setIsDismissed(true);
    saveState(true, isAppInstalled());
  }, []);

  // Determine if install button should be shown
  const showInstallButton = !isInstalled && !isDismissed && deferredPrompt !== null;

  return {
    isInstallable: deferredPrompt !== null,
    isInstalled,
    showInstallButton,
    install,
    dismissInstallPrompt
  };
}

